import React, { useState, useEffect } from 'react';
import { notification } from 'antd';
import axios from 'axios';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { API_URL, ENDPOINTS } from '../constants/api';
import '../css/modal.css';

const DetailData = ({ token, route, title, id, isOpen, onRequestClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen || !id || !route) return;

    let isMounted = true;
    setLoading(true);
    setSearchTerm('');

    const url = `${API_URL}/${ENDPOINTS.SYS}/${route}/${id}`;

    axios
      .get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
      .then((response) => {
        if (!isMounted) return;

        if (response.data?.success === 0) {
          notification.error({
            message: 'Error Loading Data',
            description: response.data.message || 'Failed to retrieve detailed record.',
          });
          setData([]);
        } else {
          setData(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error('Error fetching detail data:', error.message);
        notification.error({
          message: 'Network Error',
          description: error.message || 'Could not connect to service endpoint.',
        });
        setData([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [id, route, token, isOpen]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onRequestClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onRequestClose]);

  if (!isOpen) return null;

  // Derive column headers dynamic array
  const headers = data && data.length > 0 ? Object.keys(data[0]) : [];

  // Client-side quick filter
  const filteredData = data
    ? data.filter((row) =>
        Object.values(row).some((val) =>
          String(val ?? '').toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : [];

  const renderCellValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted opacity-50">—</span>;
    }

    const stringVal = String(value);
    // Sanitize pseudo-JSX tags if raw text contains HTML/JSX syntax
    if (stringVal.includes('<') && stringVal.includes('/>')) {
      return '';
    }

    return stringVal;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onRequestClose}
      />

      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ zIndex: 1050 }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
            
            {/* Modal Header */}
            <div className="modal-header bg-dark text-white px-4 py-3 align-items-center">
              <div className="d-flex align-items-center gap-2">
                <div className="p-2 bg-primary bg-opacity-25 rounded-circle text-primary d-flex align-items-center justify-content-center">
                  <FeatherIcon icon="list" size="18" />
                </div>
                <div>
                  <h5 className="modal-title fw-bold mb-0 text-white">{title || t('Details')}</h5>
                  <span className="small text-muted">Record Detail View</span>
                </div>
              </div>

              <button
                type="button"
                className="btn-close btn-close-white opacity-75"
                aria-label="Close"
                onClick={onRequestClose}
              />
            </div>

            {/* Sub-header / Filter Toolbar */}
            {data && data.length > 0 && (
              <div className="px-4 py-3 bg-light border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary-subtle text-primary fw-semibold rounded-pill px-3 py-2">
                    {filteredData.length} {filteredData.length === 1 ? 'Record' : 'Records'}
                  </span>
                </div>

                <div className="position-relative style-search" style={{ maxWidth: '280px', width: '100%' }}>
                  <FeatherIcon
                    icon="search"
                    size="15"
                    className="position-absolute top-50 translate-middle-y text-muted ms-3"
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-pill ps-5 bg-white border-secondary-subtle"
                    placeholder={`${t('Search')}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Modal Body */}
            <div className="modal-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted small mb-0">{t('Loading data')}...</p>
                </div>
              ) : data && data.length > 0 ? (
                <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light sticky-top shadow-sm" style={{ zIndex: 1 }}>
                      <tr>
                        {headers.map((header) => (
                          <th
                            key={header}
                            className="text-uppercase small fw-bold text-secondary px-3 py-3 border-bottom"
                            style={{ letterSpacing: '0.5px' }}
                          >
                            {t(header.replace(/_/g, ' ').toUpperCase())}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.length > 0 ? (
                        filteredData.map((item, index) => (
                          <tr key={index}>
                            {headers.map((key) => (
                              <td key={key} className="px-3 py-3 text-dark small">
                                {renderCellValue(item[key])}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={headers.length} className="text-center py-5 text-muted">
                            <FeatherIcon icon="filter" size="24" className="mb-2 opacity-50" />
                            <div>No matching records found for "{searchTerm}"</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FeatherIcon icon="inbox" size="32" className="mb-2 opacity-50" />
                  <div>{t('No data available')}.</div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="modal-footer bg-light px-4 py-2 border-top">
              <button
                type="button"
                className="btn btn-secondary rounded-pill px-4 fw-medium btn-sm"
                onClick={onRequestClose}
              >
                {t('Close')}
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default DetailData;